<!--
  Deal card (SPEC-2026-08-crm-pipeline-deals-radar).

  LAYOUT (revised 2026-08-19). The card had grown to NINE stacked rows, one per
  signal, and came out nearly square — on a deal with no score and no origin, four
  of those rows carried no information at all. Recomposed into four bands:

    A · identity   priority + flags + name, sharing one row
    B · value      product and amount, the one row that already used the full width
    C · signals    commitment | quality, two fixed slots on one row (app-card-radar)
    D · footer     owner on the left, temperature and menu on the right

  Two specific fixes behind the height win:

  - the controls used `float-end`, which does not participate in the flex flow, so
    they needed a block of their own and produced a near-empty ninth row. They now
    share band D with the owner via `justify-content-between`;
  - the absolute creation date duplicated "Xd no funil" from the radar. The
    relative form is the one that drives triage, so the absolute date moved into
    the title's tooltip instead of costing a row.

  The column is fixed-width: the horizontal room the chips now share is exactly
  what buys the vertical space back, and height is what decides how many deals fit
  on screen for the daily sweep (US-01).

  The lateral edge is the radar itself (RF-13): 30 cards in a column are triaged by
  scanning the left border, not by reading each one. The urgency label inside the
  radar states the same thing in words, so colour is never the only carrier (RNF-08).
-->
<div
  class="card border rounded my-1 shadow-sm cursor-pointer h-100"
  (click)="onCardClick($event, card)"
  [ngClass]="[card.cloned ? 'bg-dark bg-opacity-10' : '', urgencyEdgeClass]"
>
  <div
    class="card-body p-2"
    [ngClass]="{
      'border border-danger bg-danger bg-opacity-10 rounded':
        card?.priority === 'CRITICAL',
    }"
  >
    <!--
      Band A — identity. Priority, flags and name share one row.

      ALIGNMENT: the name is an `<a>` with `lh-1`, not an `<h5>`. Hyper styles every
      heading with `line-height: 1.1`, `margin-bottom: 10px`, `font-weight: 700` and
      `--ct-heading-color`; dropping a box carrying all of that into a chip row left
      the name sitting visibly lower than the badge beside it. `font-15` is the size
      the h5 already resolved to (base 0.9rem × 1.04), so nothing shrank — the box
      simply stopped bringing heading typography with it, and `lh-1` matches the
      badge's own line-height so `align-items-center` lands both on the same line.

      The flags are grouped into their own non-shrinking cluster, which leaves the
      row with exactly two children: fixed chrome, and the one element allowed to
      absorb the remaining width and truncate. Because there are only two, the
      row's `gap-2` is the flags-to-name distance and nothing else — the chips keep
      their own tighter `gap-1` inside the cluster.

      `justify-content-between` would be inert here: the name carries `flex-grow-1`,
      so it already occupies every pixel left over and there is no free space for
      `justify-content` to distribute. Separation has to come from the gap.
    -->
    <div class="d-flex align-items-center gap-2 mb-1">
      <div class="d-flex align-items-center gap-1 flex-shrink-0">
        <span
          class="badge px-2 font-11 fw-bold d-flex align-items-center justify-content-center"
          [ngClass]="{
            'bg-danger text-white':
              card?.priority === 'URGENT' || card?.priority === 'CRITICAL',
            'bg-warning text-dark': card?.priority === 'HIGH',
            'bg-info text-white': card?.priority === 'MEDIUM',
            'bg-success text-white': card?.priority === 'LOW',
            'bg-secondary text-white': card?.priority === 'NONE',
          }"
        >
          {{ translateEnums(card.priority) | translate }}
        </span>

        @if (card?.isPrivate) {
          <span
            [matTooltip]="'kanban.cardItem.privateOpportunity' | translate"
            class="z-100 d-inline-flex align-items-center"
          >
            <i class="mdi mdi-lock-outline text-danger font-16 lh-1"></i>
          </span>
        }

        @if (card?.cloned) {
          <span class="badge bg-warning text-white font-11">
            {{ 'kanban.cardItem.cloned' | translate }}
          </span>
        }

        <!--
          Informative only (SPEC-2026-10 RF-21, ADR-002): a WON or LOST deal of a blacklisted
          contact keeps counting and keeps its column; the mark just says so. An open deal of
          that contact is withdrawn from the board and never reaches this template.
        -->
        @if (isBlacklistedClosed) {
          <span
            class="badge bg-soft-dark text-dark font-11"
            [matTooltip]="'kanban.cardItem.blacklistedTooltip' | translate"
          >
            {{ 'kanban.cardItem.blacklisted' | translate }}
          </span>
        }
      </div>

      <!--
        The creation date lives in this tooltip rather than on the card: the radar
        already shows "Xd no funil", which is the same fact in the form that drives
        a decision.
      -->
      <a
        class="font-15 fw-bold lh-1 text-dark text-decoration-none text-truncate flex-grow-1 min-w-0 cursor-pointer"
        href="javascript:void(0);"
        [matTooltip]="
          ('kanban.cardItem.createdAtTooltip' | translate) +
          ' ' +
          (card.createdAt | date: 'd MMM y')
        "
      >
        {{ card.name }}
      </a>
    </div>

    <!-- Band B — value. -->
    <div class="d-flex justify-content-between text-muted gap-2 mb-1">
      <span class="text-truncate font-12">
        <i class="mdi mdi-briefcase-outline me-1"></i>
        {{ getProductsNames(card.product) || ('kanban.cardItem.noProduct' | translate) }}
      </span>

      <span class="font-12 flex-shrink-0">
        <i class="mdi mdi-cash-multiple me-1"></i>
        {{
          card.budget
            ? (card.budget | currency: "BRL" : "symbol-narrow" : "1.2-2")
            : "R$ 0,00"
        }}
      </span>
    </div>

    <!--
      Band C — the daily radar. `compact` drops every unavailable signal, so a card
      with fewer known facts is shorter instead of carrying rows that say so.
    -->
    @if (getModuleCRM()) {
      <app-card-radar
        [card]="card"
        [compact]="true"
        (scheduleNextAction)="scheduleNextAction.emit($event)"
        (openScoreExplanation)="openScoreExplanation.emit($event)"
        (concludeNextAction)="concludeNextAction.emit($event)"
      ></app-card-radar>
    }

    <!--
      Band D — footer. One flex row: owner left, controls right. Replaces the
      `float-end` block that used to occupy a row of its own.
    -->
    <div class="d-flex align-items-center justify-content-between gap-1">
      <div class="d-flex align-items-center min-w-0">
        <img
          [src]="getUserImage(card.user ?? null)"
          class="avatar-xs rounded-circle border me-2 flex-shrink-0"
          (error)="onImageError($event)"
        />
        <span class="fw-semibold text-dark font-13 text-truncate">
          {{ card.user?.name ?? ('kanban.cardItem.noAssignee' | translate) }}
        </span>
      </div>

      <div class="d-flex align-items-center flex-shrink-0">
        <!--
          Temperature is derived from qualification and health (E2 RF-22, RF-23): shown
          with its label on the tooltip, never offered for editing.
        -->
        <span
          class="d-flex align-items-center me-1"
          [matTooltip]="temperatureTooltip() | translate"
          (click)="$event.stopPropagation()"
        >
          @if (card?.temperature === CardTemperatureEnum.HOT) {
            <i class="mdi mdi-fire font-18 text-danger"></i>
          } @else if (card?.temperature === CardTemperatureEnum.WARM) {
            <i class="mdi mdi-weather-partly-cloudy font-18 text-dark text-opacity-50"></i>
          } @else if (card?.temperature === CardTemperatureEnum.COLD) {
            <i class="mdi mdi-snowflake font-18 text-info"></i>
          } @else {
            <i class="mdi mdi-thermometer font-18 text-muted"></i>
          }
        </span>

        <div class="dropdown dropdown-content" (click)="$event.stopPropagation()">
          <a
            class="dropdown-toggle text-muted arrow-none dropdown-button"
            data-bs-toggle="dropdown"
          >
            <i class="mdi mdi-dots-vertical font-16"></i>
          </a>

          <div class="dropdown-menu dropdown-menu-end">
            <a
              href="javascript:void(0);"
              class="dropdown-item"
              (click)="onEditCard(card)"
            >
              <i class="mdi mdi-pencil me-1"></i>
              {{ 'kanban.cardItem.edit' | translate }}
            </a>
            @if (stage.lostDeal !== true && stage.wonDeal !== true) {
              <a
                href="javascript:void(0);"
                class="dropdown-item"
                tabindex="-1"
                (click)="openTransferCardModal($event, card, 'move')"
              >
                <i class="mdi mdi-arrow-u-right-top"></i>
                {{ 'kanban.cardItem.moveTo' | translate }}
              </a>
            } @else {
              @if (!card.cloned) {
                <a
                  href="javascript:void(0);"
                  class="dropdown-item"
                  tabindex="-1"
                  (click)="openTransferCardModal($event, card, 'clone')"
                >
                  <i class="mdi mdi-file-multiple me-1"></i>
                  {{ 'kanban.cardItem.clone' | translate }}
                </a>
              }
            }
            @if (!isAttendant()) {
              <a
                href="javascript:void(0);"
                class="dropdown-item text-danger"
                (click)="onDeleteCard(card)"
              >
                <i class="mdi mdi-delete me-1"></i>
                {{ 'kanban.cardItem.remove' | translate }}
              </a>
            }
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
